import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Spinner } from "@phosphor-icons/react";
import snapshot from "./assessment.json";
import { flattenStatements, loadAssessment, scoreAssessment } from "./scaledeasyAssessment";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISCLAIMER = "Educational only, not professional advice. What you choose to do with these results is up to you.";
const progressKey = (token) => `cvc.assessment.progress.${token}`;

function readProgress(token) {
  try { return JSON.parse(localStorage.getItem(progressKey(token))) || {}; } catch { return {}; }
}

function useAssessmentProgress(token) {
  const saved = useMemo(() => readProgress(token), [token]);
  const [answers, setAnswers] = useState(saved.answers || {});
  const [step, setStep] = useState(saved.step === "results" ? "results" : "intro"); // "intro" | group index | "results"
  useEffect(() => {
    try { localStorage.setItem(progressKey(token), JSON.stringify({ answers, step })); } catch { /* storage disabled */ }
  }, [token, answers, step]);
  return { answers, setAnswers, step, setStep };
}

function groupsOf(assessment) {
  return assessment.pillars.flatMap((p) => p.groups.map((g) => ({ ...g, pillar: p })));
}

/** CVC-branded Business Scaling Assessment. Scored in the browser; results saved to CVC only. */
export default function CvcAssessment({ token, onDiscuss }) {
  const [assessment, setAssessment] = useState(null);
  const { answers, setAnswers, step, setStep } = useAssessmentProgress(token);

  useEffect(() => {
    let alive = true;
    loadAssessment({ snapshot }).then((a) => alive && setAssessment(a));
    return () => { alive = false; };
  }, []);

  const total = useMemo(() => (assessment ? flattenStatements(assessment).length : 0), [assessment]);
  const answered = Object.keys(answers).length;
  if (!assessment) {
    return <div className="flex items-center gap-3 p-10 font-body text-cvc-ink-soft"><Spinner className="animate-spin" /> Loading…</div>;
  }
  const groups = groupsOf(assessment);
  const scroll = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const go = (s) => { setStep(s); scroll(); };

  return (
    <div data-testid="assessment-root">
      {step !== "intro" && step !== "results" && <Progress answered={answered} total={total} />}
      {step === "intro" && <Intro assessment={assessment} answered={answered} onStart={() => go(answered ? firstOpenGroup(groups, answers) : 0)} />}
      {typeof step === "number" && groups[step] && (
        <GroupStep
          group={groups[step]} index={step} count={groups.length} options={assessment.answer_options}
          answers={answers} setAnswers={setAnswers}
          onBack={() => go(step === 0 ? "intro" : step - 1)}
          onNext={() => go(step === groups.length - 1 ? "results" : step + 1)}
        />
      )}
      {step === "results" && (
        <Results assessment={assessment} answers={answers} token={token} onDiscuss={onDiscuss} onReview={() => go(0)} />
      )}
    </div>
  );
}

function firstOpenGroup(groups, answers) {
  const i = groups.findIndex((g) => g.items.some((_, n) => answers[`${g.id}#${n}`] === undefined));
  return i < 0 ? groups.length - 1 : i;
}

function Progress({ answered, total }) {
  const pct = total ? Math.round((answered / total) * 100) : 0;
  return (
    <div className="sticky top-0 z-10 bg-cvc-cream/95 backdrop-blur px-7 md:px-10 pt-4 pb-3 border-b border-cvc-border">
      <div className="flex justify-between font-body text-xs text-cvc-ink-soft mb-1.5">
        <span>Progress</span><span data-testid="assessment-progress">{answered} / {total}</span>
      </div>
      <div className="h-1.5 rounded-full bg-cvc-border overflow-hidden">
        <div className="h-full bg-cvc-navy rounded-full transition-[width] duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Intro({ assessment, answered, onStart }) {
  return (
    <section className="p-7 md:p-10" data-testid="assessment-intro">
      <p className="text-[11px] tracking-[0.22em] uppercase font-body font-semibold text-cvc-red">Business Scaling Assessment</p>
      <h1 className="font-heading text-3xl md:text-4xl text-cvc-navy mt-3 leading-tight">How ready is your business to grow without you?</h1>
      <p className="font-body text-cvc-ink mt-4 leading-relaxed">{assessment.intro?.copy}</p>
      <div className="mt-5 flex flex-wrap gap-2">
        {["About 20 minutes", "100% free", "Progress saves on this device"].map((b) => (
          <span key={b} className="font-body text-xs px-3 py-1 rounded-full bg-white border border-cvc-border text-cvc-ink-soft">{b}</span>
        ))}
      </div>
      <Button data-testid="assessment-start" onClick={onStart}
        className="mt-7 bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-7 py-5 h-auto font-body">
        {answered ? "Pick up where I left off" : "Start the assessment"} <ArrowRight size={16} weight="bold" className="ml-2" />
      </Button>
    </section>
  );
}

function GroupStep({ group, index, count, options, answers, setAnswers, onBack, onNext }) {
  const complete = group.items.every((_, n) => answers[`${group.id}#${n}`] !== undefined);
  const set = (id, v) => setAnswers((prev) => ({ ...prev, [id]: v }));
  return (
    <section className="p-7 md:p-10" data-testid={`assessment-group-${index}`}>
      <p className="font-body text-[11px] tracking-[0.2em] uppercase text-cvc-ink-soft">
        {group.pillar.label} · Section {index + 1} of {count}
      </p>
      <h2 className="font-heading text-2xl md:text-3xl text-cvc-navy mt-1.5">{group.title}</h2>
      <p className="font-body text-sm text-cvc-ink-soft mt-1">{group.pillar.question}</p>
      <div className="mt-6 space-y-3">
        {group.items.map((text, n) => {
          const id = `${group.id}#${n}`;
          return (
            <div key={id} data-testid={`assessment-q-${id}`} className="rounded-xl border border-cvc-border bg-white p-4 flex flex-col md:flex-row md:items-center gap-3">
              <p className="flex-1 font-body text-[15px] text-cvc-ink leading-relaxed">{text}</p>
              <div className="flex gap-1.5 shrink-0" role="group">
                {options.map((o) => {
                  const on = answers[id] === o.value;
                  return (
                    <button key={o.value} type="button" aria-pressed={on} onClick={() => set(id, o.value)}
                      data-testid={`assessment-opt-${id}-${o.value}`}
                      className={`font-body text-xs font-semibold px-3.5 py-2.5 rounded-full border transition-colors ${on ? "bg-cvc-navy border-cvc-navy text-cvc-cream" : "bg-white border-cvc-border text-cvc-ink-soft hover:border-cvc-navy"}`}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-7 flex items-center justify-between gap-3">
        <button type="button" data-testid="assessment-back" onClick={onBack} className="flex items-center gap-1.5 font-body text-sm text-cvc-ink-soft hover:text-cvc-ink">
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <Button data-testid="assessment-next" onClick={onNext} disabled={!complete}
          className="bg-cvc-navy hover:bg-cvc-navy-deep text-cvc-cream rounded-full px-6 py-4 h-auto font-body disabled:opacity-40">
          {!complete ? "Answer all to continue" : index === count - 1 ? "See my scorecard" : "Next"}
        </Button>
      </div>
    </section>
  );
}

function Results({ assessment, answers, token, onDiscuss, onReview }) {
  const result = useMemo(() => scoreAssessment(assessment, answers), [assessment, answers]);
  useEffect(() => {
    if (!result.complete) return;
    fetch(`${API}/next-move/assessment-results`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token, overall_pct: result.overallPct, verdict: result.verdict,
        pillars: result.pillars.map(({ id, label, pct }) => ({ id, label, pct })),
        groups: result.groups.map(({ id, title, pct }) => ({ id, title, pct })),
        weakest: result.weakestGroups.map(({ id, title, pct }) => ({ id, title, pct })),
        answers,
      }),
    }).catch((e) => console.warn("Couldn't save assessment results", e?.message));
  }, [result, token, answers]);

  return (
    <section className="p-7 md:p-10" data-testid="assessment-results">
      <p className="text-[11px] tracking-[0.22em] uppercase font-body font-semibold text-cvc-red">Your scaling scorecard</p>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-4">
        <span data-testid="assessment-overall" className="font-heading text-6xl text-cvc-navy">{result.overallPct}%</span>
        <span data-testid="assessment-verdict" className="font-heading text-xl text-cvc-ink">{result.verdict}</span>
      </div>
      <div className="mt-7 space-y-3">
        {result.pillars.map((p) => (
          <div key={p.id} data-testid={`assessment-pillar-${p.id}`}>
            <div className="flex justify-between font-body text-sm text-cvc-ink"><span>{p.label}</span><strong>{p.pct}%</strong></div>
            <div className="mt-1 h-2.5 rounded-full bg-cvc-border overflow-hidden">
              <div className="h-full bg-cvc-navy rounded-full" style={{ width: `${Math.max(2, p.pct)}%` }} />
            </div>
          </div>
        ))}
      </div>
      <p className="mt-7 font-heading text-lg text-cvc-navy">Your three weakest areas</p>
      <ol data-testid="assessment-weakest" className="mt-2 list-decimal pl-5 font-body text-cvc-ink space-y-1">
        {result.weakestGroups.map((g) => <li key={g.id}>{g.title} <span className="text-cvc-ink-soft">({g.pct}%)</span></li>)}
      </ol>
      <p className="mt-6 font-body text-sm text-cvc-ink-soft leading-relaxed">{assessment.closing_copy}</p>
      <div className="mt-8 rounded-2xl bg-cvc-navy text-cvc-cream p-6">
        <p className="font-heading text-xl">Scores are a starting point. The conversation is where it gets useful.</p>
        <p className="font-body text-sm text-cvc-cream/80 mt-2">Talk through your scorecard with Jonathan in a free 20-minute conversation. No obligation.</p>
        <Button data-testid="assessment-discuss" onClick={onDiscuss}
          className="mt-5 bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-7 py-5 h-auto font-body">
          Discuss these insights with Jonathan
        </Button>
      </div>
      <button type="button" data-testid="assessment-review" onClick={onReview} className="mt-5 font-body text-sm text-cvc-ink-soft underline underline-offset-4">
        Review my answers
      </button>
      <p className="mt-6 pt-4 border-t border-cvc-border font-body text-xs italic text-cvc-ink-soft">{DISCLAIMER}</p>
    </section>
  );
}
